import { Link } from "react-router-dom";
import { Smartphone, RefreshCw, Wrench, Apple, HeartHandshake, Star } from "lucide-react";
import { business, fullAddress } from "@/config/business";
import { images } from "@/config/site";
import { Reveal } from "@/components/ui-motion/Reveal";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { LocationSection } from "@/pages/Home";
import { useSeo } from "@/lib/seo";

const focus = [
  { icon: Smartphone, t: "New & Pre-Owned Phones", d: "A carefully chosen range of new and quality pre-owned smartphones from all the major brands." },
  { icon: Wrench, t: "Mobile Repair", d: "Screens, batteries, charging, cameras, speakers, software and water-damage repairs." },
  { icon: Apple, t: "Apple Repair", d: "Specialist attention for iPhone and iPad devices." },
  { icon: HeartHandshake, t: "Local Customer Service", d: "Friendly, honest, face-to-face help — right here in Sector 3, Rohini." },
];

export default function About() {
  useSeo({
    title: "About Mobile Specialist — Your Local Mobile Shop in Rohini",
    description: "Mobile Specialist in Sector 3, Rohini, Delhi: new & pre-owned smartphones, mobile and Apple repair, and honest local service. Rated 4.9/5 on Google.",
    path: "/about",
    image: images.storeInterior,
  });
  return (
    <div data-testid="about-page">
      <section className="container-wide py-14">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[hsl(var(--ink-soft))]">About Us</p>
            <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Your Local Mobile Specialist in Rohini</h1>
            <p className="mt-5 leading-relaxed text-[hsl(var(--ink-soft))]">
              Mobile Specialist is a trusted local smartphone shop in Sector 3, Rohini, Delhi. We help our
              neighbours buy new and pre-owned phones, sell devices they no longer need, and get their
              phones repaired — all in one place.
            </p>
            <p className="mt-4 leading-relaxed text-[hsl(var(--ink-soft))]">
              From screen and battery replacements to charging, cameras, software and specialist Apple
              repairs, our focus is simple: honest advice, careful work and dependable service for every
              customer who walks through our door.
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm font-medium">
              <Star className="h-4 w-4 fill-[hsl(var(--accent))] accent-text" /> Rated {business.googleRating}/5 across {business.googleReviewCount} Google reviews
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/phones" className="btn-ink">Browse Phones</Link>
              <WhatsAppButton label="WhatsApp Us" className="btn-outline" />
            </div>
          </div>
          <Reveal className="overflow-hidden rounded-3xl border border-border">
            <img src={images.storeInterior} alt="Inside Mobile Specialist store, Rohini" className="h-full w-full object-cover" loading="lazy" />
          </Reveal>
        </div>
      </section>

      <section className="container-wide">
        <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
          {focus.map((f) => (
            <div key={f.t} className="bg-card p-6">
              <f.icon className="h-6 w-6 accent-text" />
              <h3 className="mt-3 font-semibold">{f.t}</h3>
              <p className="mt-1.5 text-sm text-[hsl(var(--ink-soft))]">{f.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-[hsl(var(--ink-soft))]">{fullAddress} · {business.hoursSummary}</p>
      </section>

      <LocationSection />
    </div>
  );
}
